import { AGUIconName } from 'agu_app_shell';

/**
 * The product-state copy (PD §5, spec §J), verbatim from P/proto-states.js with line cites, in one place so every screen says the
 * same words. Truthfulness edits (spec §J "Copy truthfulness") — each departs from the prototype line named:
 *   - :95  Home caught up: "We check every night and email you when something fits." → "We check every night." (no email alerts exist)
 *   - :97  Finder all-empty: the "Save as an alert" button is dropped (no alerts feature)
 *   - :98  Finder recommended-empty: "… added nightly and we will email you the moment one matches." → "… added nightly."
 *   - :104 Saved empty: "… waits for you here, with deadline reminders." → "… waits for you here." (no reminders are sent)
 * The prototype's error ref "Error 503 · ref 7F2A-19" (:65) is a placeholder: the foot is built from the real error (request-id.ts).
 */

/** One panel's words and glyph. */
export interface StatePanelCopy {
    readonly Icon: AGUIconName;
    readonly Title: string;
    readonly Body: string;
}

/** The noun each list screen names itself by (P/proto-states.js:53, `NOUN`). */
export const STATE_NOUN = {
    Grants: 'grants',
    People: 'people',
    Jobs: 'jobs',
    Search: 'results',
    Saved: 'saved items',
} as const;

/** The Home sub line while Home reloads (:128). */
export const REFRESHING_MATCHES_SUB = 'Refreshing your matches…';
/** The `.ps-note` after a second consecutive failure (:236). */
export const STILL_NOT_LOADING_NOTE = 'Still not loading. If this keeps happening, check AGU’s service status page.';

export function BrowseAllLabel(noun: string): string {
    return `Browse all ${noun}`; // :98
}

/** Finder All tab with zero results (:97; "Save as an alert" dropped). */
export function FinderAllEmptyCopy(noun: string): StatePanelCopy {
    return { Icon: 'search', Title: `No open ${noun} match these filters`, Body: 'Try removing a filter or widening the location. Your search is kept.' };
}

/** Finder Recommended tab with nothing in it (:98, email clause dropped). */
export function FinderRecommendedEmptyCopy(noun: string): StatePanelCopy {
    return { Icon: 'inbox', Title: 'No new recommendations this week', Body: `Nothing open fits your research areas right now. New ${noun} are added nightly.` };
}

/** Search everything with no results (:101). */
export const SEARCH_EMPTY_COPY: StatePanelCopy = {
    Icon: 'search',
    Title: 'No results for that search',
    Body: 'Check the spelling, use fewer words, or try a broader topic such as “hydrology”.',
};

/** The Saved board with nothing saved (:104, reminders clause dropped). */
export const SAVED_EMPTY_COPY: StatePanelCopy = { Icon: 'inbox', Title: 'Nothing saved yet', Body: 'Bookmark a grant, person or job and it waits for you here.' };

/** Home on a returning visit with nothing new since the last one (:95, email clause dropped). */
export const HOME_CAUGHT_UP_COPY: StatePanelCopy = { Icon: 'check', Title: 'You are all caught up', Body: 'No new matches since your last visit. We check every night.' };

/** A list that failed to load (:115). */
export function ListErrorCopy(noun: string): StatePanelCopy {
    return { Icon: 'alert', Title: `We could not load ${noun}`, Body: 'Something went wrong on our side, not yours. Your search and filters are kept, so nothing is lost.' };
}

/**
 * A single record or page that failed to load (detail, profile, settings). The prototype gives these screens a 404 panel and a
 * save banner but no load-failure panel (:120-124), so this is the list error's heading and first sentence with the screen's own
 * noun; the "search and filters are kept" sentence is left off because these screens have neither.
 */
export function PageErrorCopy(noun: string): StatePanelCopy {
    return { Icon: 'alert', Title: `We could not load ${noun}`, Body: 'Something went wrong on our side, not yours.' };
}

/** A detail whose record is gone (:121). */
export const LISTING_GONE_COPY: StatePanelCopy = {
    Icon: 'inbox',
    Title: 'This listing is no longer available',
    Body: 'The poster closed or removed it. Similar openings are still live.',
};

/** People Finder's lapsed-membership lock (:137, `nm + ' is a member benefit'`). */
export const PEOPLE_LOCK_COPY: StatePanelCopy = {
    Icon: 'lock',
    Title: 'People Finder is a member benefit',
    Body: 'Renew to search members by expertise and see mutual co-authors.',
};

/** Career Explorer's lapsed-membership lock (P/proto-states.js:137), dormant until the token claims say lapsed. */
export const CAREER_LOCK_COPY: StatePanelCopy = {
    Icon: 'lock',
    Title: 'Career Explorer is a member benefit',
    Body: 'Renew to see career paths matched to your CV. Your CV and results are kept for 90 days.',
};

/** The tile names Home's partial-failure banner uses, in the prototype's order (:111). */
export type HomeTileKind = 'grant' | 'person' | 'job';
const HOME_TILE_NAME: Readonly<Record<HomeTileKind, string>> = { grant: 'Grants', person: 'People', job: 'Jobs' };
const HOME_TILE_ORDER: readonly HomeTileKind[] = ['grant', 'person', 'job'];

/** "A", "A and B", "A, B and C". */
function JoinNames(names: readonly string[]): string {
    return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Home's partial-failure sentence, split so the failed part can be bold (:111). */
export interface HomePartialFailureCopy {
    /** "Part of your dashboard did not load. Grants and People are up to date; " (the up-to-date clause omitted when nothing is). */
    readonly Lead: string;
    /** "Jobs could not refresh" — rendered in `<b>`, followed by ".". */
    readonly Failed: string;
}

/**
 * The partial-failure banner (:111): `shown` are the tiles on screen, `failed` the kinds the server could not refresh. Null when
 * no shown tile failed.
 */
export function HomePartialFailure(shown: readonly HomeTileKind[], failed: readonly HomeTileKind[]): HomePartialFailureCopy | null {
    const failedShown = HOME_TILE_ORDER.filter((k) => shown.includes(k) && failed.includes(k));
    if (failedShown.length === 0) return null;
    const fine = HOME_TILE_ORDER.filter((k) => shown.includes(k) && !failed.includes(k)).map((k) => HOME_TILE_NAME[k]);
    const upToDate = fine.length === 0 ? '' : ` ${JoinNames(fine)} ${fine.length === 1 ? 'is' : 'are'} up to date;`;
    return {
        Lead: `Part of your dashboard did not load.${upToDate} `,
        Failed: `${JoinNames(failedShown.map((k) => HOME_TILE_NAME[k]))} could not refresh`,
    };
}

/** The pill a failed tile carries (:112). */
export const NOT_REFRESHED_PILL = 'Not refreshed';
